import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { getMeta } from "./calendarTypes";
import {
  HOURS, HOUR_PX, buildWeekDays, eventGeometry, formatHM,
  FR_DAYS_SHORT, isSameDay, gmtOffsetLabel,
} from "./calendarHelpers";

/**
 * WeekView — desktop weekly grid (08:00 → 20:00, Mon → Sun).
 * Events are positioned absolutely inside each day column using their start/end times.
 * A red horizontal "now" line marks the current time when the week contains today.
 */
export default function WeekView({ weekStart, events, onEventClick, onSlotClick }) {
  const days = useMemo(() => buildWeekDays(weekStart), [weekStart]);
  const today = useMemo(() => new Date(), []);
  const eventsByDay = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d.toDateString()] = []; });
    events.forEach((e) => {
      const k = new Date(e.starts_at).toDateString();
      if (m[k]) m[k].push(e);
    });
    return m;
  }, [events, days]);

  // Live "now" indicator
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const showNow = days.some((d) => isSameDay(d, now));
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const dayStartMin = HOURS[0] * 60;
  const dayEndMin = (HOURS[HOURS.length - 1] + 1) * 60;
  const nowTop = ((nowMinutes - dayStartMin) / 60) * HOUR_PX;
  const nowVisible = nowMinutes >= dayStartMin && nowMinutes <= dayEndMin;

  // Auto-scroll to a reasonable area on mount
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(0, nowTop - 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gridHeight = HOURS.length * HOUR_PX;

  return (
    <div
      className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 overflow-hidden"
      data-testid="cal-week-view"
    >
      {/* Header row — days */}
      <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-border dark:border-border bg-secondary/50 dark:bg-surface/60">
        <div className="px-2 py-3 text-[10px] uppercase tracking-wider text-muted-foreground flex items-end justify-end pr-3">
          {gmtOffsetLabel()}
        </div>
        {days.map((d) => {
          const isToday = isSameDay(d, today);
          return (
            <div
              key={d.toISOString()}
              className="px-3 py-3 flex items-center justify-center gap-2 border-l border-border/70 dark:border-border"
              data-testid={`week-day-header-${d.toISOString().slice(0, 10)}`}
            >
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {FR_DAYS_SHORT[(d.getDay() + 6) % 7]}.
              </span>
              <span className={cn(
                "tabular-nums text-sm font-semibold flex items-center justify-center",
                isToday
                  ? "h-7 w-7 rounded-full bg-violet-500 text-white"
                  : "text-foreground/90"
              )}>
                {d.getDate()}
              </span>
            </div>
          );
        })}
      </div>

      {/* Grid body */}
      <div ref={scrollRef} className="overflow-y-auto max-h-[calc(100vh-340px)] min-h-[480px]">
        <div className="relative grid grid-cols-[64px_repeat(7,1fr)]" style={{ height: gridHeight }}>
          {/* Hours column */}
          <div className="relative border-r border-border/70 dark:border-border">
            {HOURS.map((h, i) => (
              <div
                key={h}
                className="absolute left-0 right-0 px-2 text-[11px] tabular-nums text-muted-foreground dark:text-muted-foreground/80 -translate-y-1/2"
                style={{ top: i * HOUR_PX }}
              >
                <span className="block text-right pr-3">{String(h).padStart(2, "0")}:00</span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((d) => {
            const items = eventsByDay[d.toDateString()] || [];
            const isToday = isSameDay(d, today);
            return (
              <div
                key={d.toISOString()}
                className="relative border-l border-border/70 dark:border-border"
                data-testid={`week-day-col-${d.toISOString().slice(0, 10)}`}
              >
                {/* Hour grid lines + slot click areas */}
                {HOURS.map((h, i) => (
                  <button
                    key={h}
                    onClick={() => {
                      const slot = new Date(d);
                      slot.setHours(h, 0, 0, 0);
                      onSlotClick?.(slot);
                    }}
                    className="absolute left-0 right-0 border-t border-border/70 dark:border-border hover:bg-accent/40 dark:hover:bg-secondary/60 transition-colors"
                    style={{ top: i * HOUR_PX, height: HOUR_PX }}
                    aria-label={`Créer un événement à ${h}:00`}
                  />
                ))}
                {/* Current time line */}
                {showNow && isToday && nowVisible && (
                  <div
                    className="pointer-events-none absolute left-0 right-0 z-20"
                    style={{ top: nowTop }}
                    data-testid="week-now-line"
                  >
                    <div className="relative h-[2px] bg-red-500/80">
                      <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                    </div>
                  </div>
                )}
                {/* Events */}
                {items.map((ev) => {
                  const { top, height } = eventGeometry(ev.starts_at, ev.ends_at);
                  const meta = getMeta(ev.type);
                  const Icon = meta.Icon;
                  return (
                    <button
                      key={ev.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(ev); }}
                      className={cn(
                        "absolute left-[var(--calendar-event-x-gap)] right-[var(--calendar-event-x-gap)] z-10 rounded-lg border-l-[3px] px-2 py-1.5 text-left transition-all duration-150",
                        "backdrop-blur-sm overflow-hidden flex flex-col gap-0.5",
                        meta.bg, meta.border, meta.text,
                      )}
                      style={{ top, height }}
                      data-testid={`week-event-${ev.id}`}
                    >
                      <div className="flex items-center justify-between gap-1 min-w-0">
                        <span className="text-[11px] font-semibold truncate leading-tight">{ev.title}</span>
                        <Icon className="h-3 w-3 shrink-0 opacity-60" />
                      </div>
                      <span className="text-[10px] opacity-80 tabular-nums leading-tight">
                        {formatHM(ev.starts_at)} – {formatHM(ev.ends_at)}
                      </span>
                      {ev.location && height > 56 && (
                        <span className="text-[10px] opacity-70 truncate leading-tight">{ev.location}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
